import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDoctorProfile,
  updateDoctorProfile,
  uploadDocuments,
} from "../../features/doctors/profileSlice";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Avatar,
  IconButton,
  Divider,
  InputAdornment,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";

const Profile = () => {
  const dispatch = useDispatch();
  const { data: profileData, loading } = useSelector((state) => state.profile);
  const [profile, setProfile] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    dispatch(fetchDoctorProfile());
  }, [dispatch]);

  useEffect(() => {
    if (profileData) {
      setProfile({
        ...profileData,
        phone: profileData.user?.phone_number || "",
        email: profileData.user?.email || "",
        name: profileData.user?.name || "",
      });
    }
  }, [profileData]);

  const handleInputChange = (e) => {
    setProfile((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSave = () => {
    dispatch(updateDoctorProfile(profile));
  };

  const handleFileUpload = (e) => {
    const formData = new FormData();
    Array.from(e.target.files).forEach((file) => formData.append("documents", file));
    dispatch(uploadDocuments(formData));
  };

  const toggleShowPassword = () => setShowPassword((prev) => !prev);

  if (loading) return <CircularProgress sx={{ display: "block", margin: "auto" }} />;
  if (!profile) return <Typography>Loading profile...</Typography>;

  return (
    <Box sx={{ maxWidth: 800, mx: "auto", p: 4, bgcolor: "white", borderRadius: 3, boxShadow: 3 }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Doctor Profile
      </Typography>

      <Grid container spacing={3}>
        {/* Profile Image */}
        <Grid item xs={12} md={4} sx={{ textAlign: "center" }}>
          <Avatar src={profile?.photo || "/default-avatar.png"} sx={{ width: 120, height: 120, mx: "auto", mb: 2 }} />
          <Button variant="outlined" component="label">
            Change Profile Image
            <input type="file" hidden onChange={(e) => setProfile({ ...profile, photo: URL.createObjectURL(e.target.files[0]) })} />
          </Button>
        </Grid>

        {/* Profile Information */}
        <Grid item xs={12} md={8}>
          <TextField fullWidth label="Name" name="name" value={profile.name || ""} onChange={handleInputChange} margin="normal" />
          <TextField fullWidth label="Email" name="email" value={profile.email || ""} onChange={handleInputChange} margin="normal" />
          <TextField fullWidth label="Phone" name="phone" value={profile.phone || ""} onChange={handleInputChange} margin="normal" />
          <TextField fullWidth label="Address" name="address" value={profile.address || ""} onChange={handleInputChange} margin="normal" />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Clinic Information */}
      {profile.clinic && (
        <>
          <Typography variant="h6">Clinic Information</Typography>
          <TextField fullWidth label="Clinic Name" name="clinicName" value={profile.clinic.clinic_name || ""} disabled margin="normal" />
          <TextField fullWidth label="Clinic Address" name="clinicAddress" value={profile.clinic.address || ""} disabled margin="normal" />
          <TextField fullWidth label="Specialization" name="specialization" value={profile.clinic.specialization || ""} disabled margin="normal" />
        </>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Document Upload */}
      <Typography variant="h6">Upload Documents for Verification</Typography>
      <Button variant="contained" component="label" startIcon={<CloudUploadIcon />}>
        Upload Documents
        <input type="file" hidden multiple onChange={handleFileUpload} />
      </Button>
      <List sx={{ mt: 2 }}>
        {profileData?.verification?.medical_license && (
          <ListItem>
            <ListItemText primary="Medical License Uploaded" />
          </ListItem>
        )}
        {profileData?.verification?.id_proof && (
          <ListItem>
            <ListItemText primary="ID Proof Uploaded" />
          </ListItem>
        )}
      </List>

      <Divider sx={{ my: 3 }} />

      {/* Change Password */}
      <Typography variant="h6">Change Password</Typography>
      <TextField fullWidth label="New Password" type={showPassword ? "text" : "password"} margin="normal"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={toggleShowPassword}>
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {/* Save Button */}
      <Button variant="contained" color="primary" onClick={handleSave} sx={{ mt: 3 }}>
        Save Changes
      </Button>
    </Box>
  );
};

export default Profile;
